import log from 'loglevel';
import FlvPlayer from './flv/FlvPlayer';
import HlsPlayer from './hls/HlsPlayer';
import NativePlayer from './native/NativePlayer';
import FlashPlayer from './flash/FlashPlayer';
import TxrtcPlayer from './txrtc/TxrtcPlayer.js';
import { mixin } from '../utils/util';
import { loadScript } from '../utils/Dom';
import PlayerAPI from '../api/PlayerAPI';

/**
 *
 * Distributed under the MIT license. See LICENSE file for details.
 * Copyright (c) 2019 vhall
 */

/**
 * 抽象初始化器
 *
 * @class AbstractInitial
 */
class AbstractInitial {
  constructor(app) {
    this._context = null;
    this._option = null;
    this.app = app; // => VideoModule instance
  }

  init(context, option = {}) {
    this._context = context;
    this._option = option;
  }

  /**
   * 创建flv播放器
   *
   * @returns
   * @memberof AbstractInitial
   */
  createFlvPlayer() {
    if (!this.isLive) {
      log.warn('非直播不可使用flv格式！');
      return null;
    }

    if (!this.supportMSE) {
      log.warn('使用flv格式，但当前环境不支持MSE！');
      return null;
    }

    this.option.type = 'flv';
    this.option.store = this.app.store;
    log.info('内核使用FlvPlayer');
    let player = new FlvPlayer(this.option, this.option);
    player.initVideo(this.option);
    player._owner = this.app;
    player.setStore(this.app.store);
    mixin(this.app, PlayerAPI, player);
    player.initEvents();
    return player;
  }

  /**
   * 创建hls播放器
   *
   * @returns
   * @memberof AbstractInitial
   */
  async createHlsPlayer() {
    var video = document.createElement('video');
    if (this.supportMSE) {
      this.option.store = this.app.store;
      log.info('内核使用HlsPlayer');
      // 检测是否开启peer5，开启时加载js
      let { open, customerId, fallback } = this.option.peer5Option;
      let peer5CustomerId = open ? customerId : '';
      if (peer5CustomerId) {
        try {
          await loadScript('script-peer5', `https://peer5.vhallyun.com/peer5.js?id=${peer5CustomerId}`);
          await loadScript('script-peer5-hls', 'https://peer5.vhallyun.com/peer5.hlsjs.loader.js');
        } catch (err) {
          log.error('peer5 sdk load error: ' + err.message);
          if (!fallback) throw Error('peer5 sdk load error: ' + err.message);
        }
        // 检测状态：已初始化或销毁
        if (window.peer5) {
          if (!window.peer5.isInitialized()) {
            window.peer5.init();
          }
        }
      }
      let player = new HlsPlayer({
        ...this.option,
        ...(peer5CustomerId
          ? {
              fLoader: window.peer5 && window.peer5.HlsJsFragmentLoader,
              pLoader: window.peer5 && window.peer5.HlsJsPlaylistLoader
            }
          : {})
      });
      player.initVideo(this.option);
      player._owner = this.app;
      player.setStore(this.app.store);
      mixin(this.app, PlayerAPI, player);
      player.initEvents();
      return player;
    } else if (video.canPlayType('application/vnd.apple.mpegURL')) {
      return this.createNativePlayer();
    } else {
      log.error('该浏览器不支持HLS-MSE播放和HLS原生播放');
      return;
    }
  }

  /**
   * 创建本地原生播放器
   *
   * @returns
   * @memberof AbstractInitial
   */
  createNativePlayer() {
    this.option.store = this.app.store;
    log.info('内核使用NativePlayer');
    let player = new NativePlayer(this.option);
    player.initVideo(this.option);
    player._owner = this.app;
    player.setStore(this.app.store);
    mixin(this.app, PlayerAPI, player);
    player.initEvents();
    return player;
  }

  /**
   * 创建flash播放器
   *
   * @returns flashPlayer
   * @memberof AbstractInitial
   */
  createFlashPlayer() {
    this.option.store = this.app.store;
    log.info('内核使用FlashPlayer');
    let player = new FlashPlayer();
    player.initFlash(this.option);
    player._owner = this.app;
    player.setStore(this.app.store);
    mixin(this.app, PlayerAPI, player);
    player.initEvents();
    return player;
  }

  async createTxRtcPlayer() {
    log.info('内核使用TXLivePlayer');
    let player = new TxrtcPlayer(this.option);
    await player.initVideo(this.option);
    player._owner = this.app;
    player.setStore(this.app.store);
    mixin(this.app, PlayerAPI, player);
    player.initEvents();
    return player;
  }

  get context() {
    return this._context || {};
  }

  get supportMSE() {
    return this.context['supportMSE'];
  }

  get option() {
    return this._option;
  }

  get isLive() {
    return this._option['isLive'];
  }
}

/**
 * 直播时自动判断初始化，根据支持情况进行初始化
 * 点播不支持flv，基本上自动判断初始化规则只用在直播上
 *
 * @export
 * @class AutoDetectInitial
 * @extends {AbstractInitial}
 */
export class AutoDetectInitial extends AbstractInitial {
  constructor(app) {
    super(app);
  }

  init(context, option = {}) {
    super.init(context, option);
    let player;
    var video = document.createElement('video');
    if (this.supportMSE) {
      player = this.createFlvPlayer();
    } else if (video.canPlayType('application/vnd.apple.mpegURL')) {
      this.option.type = 'hls';
      player = this.createNativePlayer();
    } else {
      log.error('该浏览器不支持FLV播放和HLS原生播放');
    }

    return player;
  }
}

/**
 * 用户自定义初始化器
 *
 * @export
 * @class UserCustomInitial
 * @extends {AbstractInitial}
 */
export class UserCustomInitial extends AbstractInitial {
  constructor(app) {
    super(app);
  }

  async init(context, option = {}) {
    super.init(context, option);
    let player = null;
    switch (option['type']) {
      case 'flv':
        player = this.createFlvPlayer();
        break;
      case 'hls':
        player = await this.createHlsPlayer();
        break;
      case 'native':
        player = this.createNativePlayer();
        break;
      case 'rtl':
        player = await this.createTxRtcPlayer();
        break;
      default:
        break;
    }
    return player;
  }
}

/**
 * flashPlayer初始化器
 *
 * @export
 * @class FlashInitial
 * @extends {AbstractInitial}
 */
export class FlashInitial extends AbstractInitial {
  constructor(app) {
    super(app);
  }

  init(context, option = {}) {
    super.init(context, option);
    switch (option['type']) {
      case 'auto':
        this.option.type = 'rtmp';
        break;
      case 'rtmp':
        this.option.type = 'rtmp';
        break;
      case 'flv':
        this.option.type = 'flv';
        break;
      case 'hls':
        this.option.type = 'hls';
        break;
      case 'native':
        this.option.type = 'native';
        break;
      default:
        break;
    }
    return this.createFlashPlayer();
  }
}
